import React, { useCallback, useEffect, useState } from 'react';
import { CButton, CSpinner, CBadge } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilDevices, cilScreenDesktop } from '@coreui/icons';
import * as DomicilappService from '../../services/DomicilappService';
import { UserSession } from '../../types/session';
import { showToastTR } from '../../Utils/BaseApp';
import { userLogout } from '../../Utils/Auth';

// "Chrome en Windows" a partir del User-Agent (suficiente para reconocer el dispositivo)
const describeBrowser = (userAgent: string | null) => {
    const ua = userAgent ?? '';
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
    const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} en ${os}` : browser;
};

const describeSession = (session: UserSession) => {
    if (session.client === 'app') {
        const device = [session.device_model, session.device_os].filter(Boolean).join(' · ');
        return { title: `App DomicilApp${device ? ` — ${device}` : ''}`, detail: session.app_mobile_version ? `Versión ${session.app_mobile_version}` : null, icon: cilDevices };
    }
    return { title: `Portal web — ${describeBrowser(session.user_agent)}`, detail: null, icon: cilScreenDesktop };
};

const formatDate = (value: string | null) => {
    if (!value) return '—';
    const date = new Date(value.replace(' ', 'T'));
    return isNaN(date.getTime()) ? value : date.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
};

export const SessionsTab: React.FC = () => {
    const [sessions, setSessions] = useState<UserSession[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);

    const loadSessions = useCallback(() => {
        setLoading(true);
        DomicilappService.getUserSessions()
            .then((response) => setSessions(response?.data ?? []))
            .catch(() => setSessions([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    const revoke = (session: UserSession) => {
        if (session.is_current) {
            userLogout();
            return;
        }
        setBusyId(session.id);
        DomicilappService.revokeUserSession(session.id)
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR('Sesión cerrada', 2500, 'success');
                    setSessions((prev) => prev.filter((s) => s.id !== session.id));
                } else {
                    showToastTR(response?.message ?? 'No fue posible cerrar la sesión', 3000, 'error');
                }
            })
            .catch(() => showToastTR('No fue posible cerrar la sesión', 3000, 'error'))
            .finally(() => setBusyId(null));
    };

    const revokeOthers = () => {
        setBusyId('others');
        DomicilappService.revokeOtherUserSessions()
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR(`Se cerraron ${response.data?.revoked ?? 0} sesiones`, 2500, 'success');
                    setSessions((prev) => prev.filter((s) => s.is_current));
                } else {
                    showToastTR(response?.message ?? 'No fue posible cerrar las sesiones', 3000, 'error');
                }
            })
            .catch(() => showToastTR('No fue posible cerrar las sesiones', 3000, 'error'))
            .finally(() => setBusyId(null));
    };

    const others = sessions.filter((s) => !s.is_current);

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <p className="text-body-secondary small mb-0">
                    Dispositivos donde tu cuenta tiene la sesión iniciada: portal web y app. Si no reconoces alguno, ciérralo.
                </p>
                {others.length > 0 && (
                    <CButton size="sm" color="light" disabled={busyId !== null} onClick={revokeOthers}>
                        {busyId === 'others' && <CSpinner size="sm" className="me-1" />}
                        Cerrar las demás sesiones
                    </CButton>
                )}
            </div>

            {loading ? (
                <div className="text-center py-4">
                    <CSpinner size="sm" className="me-2" />
                    <span className="text-body-secondary">Cargando sesiones...</span>
                </div>
            ) : sessions.length === 0 ? (
                <p className="text-body-secondary small py-3 mb-0">
                    No hay sesiones registradas. Las sesiones aparecen aquí a partir del próximo inicio de sesión.
                </p>
            ) : (
                sessions.map((session) => {
                    const info = describeSession(session);
                    return (
                        <div key={session.id} className="border rounded-3 p-3 mb-2 d-flex justify-content-between align-items-center gap-3 bg-body">
                            <div className="d-flex align-items-start gap-3 min-w-0">
                                <CIcon icon={info.icon} size="lg" className="text-body-secondary mt-1" />
                                <div className="min-w-0">
                                    <div className="fw-semibold text-body d-flex align-items-center gap-2 flex-wrap">
                                        {info.title}
                                        {session.is_current && <CBadge color="success-subtle" className="text-success">Este dispositivo</CBadge>}
                                    </div>
                                    <small className="text-body-secondary d-block">
                                        Última actividad: {formatDate(session.last_activity_at)}
                                        {session.ip_address ? ` · IP ${session.ip_address}` : ''}
                                        {info.detail ? ` · ${info.detail}` : ''}
                                    </small>
                                    <small className="text-body-secondary d-block">Inició: {formatDate(session.created_at)}</small>
                                </div>
                            </div>
                            <CButton size="sm" color="light" className="text-nowrap" disabled={busyId !== null} onClick={() => revoke(session)}>
                                {busyId === session.id && <CSpinner size="sm" className="me-1" />}
                                {session.is_current ? 'Cerrar sesión' : 'Cerrar'}
                            </CButton>
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default SessionsTab;
